import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '../components/ui/table';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Search,
  Plus,
  Edit,
  Trash2,
  Download,
  Filter,
  DollarSign,
  ArrowDown,
  ArrowUp,
  Calendar,
  Clock,
  X,
  Loader2,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  History
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { PricingAPI, ProductPricing, TimeBasedDiscount } from '../lib/api';
import { toast } from '../components/ui/use-toast';

// Discount Details Component
interface DiscountDetailsProps {
  product: ProductPricing;
  onClose: () => void;
  onDiscountUpdate: () => void;
}

function DiscountDetails({ product, onClose, onDiscountUpdate }: DiscountDetailsProps) {
  const [discounts, setDiscounts] = useState<TimeBasedDiscount[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [newDiscount, setNewDiscount] = useState({
    discountPercentage: '',
    effectiveFrom: '',
    effectiveTo: '',
    reason: ''
  });

  useEffect(() => {
    fetchDiscounts();
  }, [product._id]);

  const fetchDiscounts = async () => {
    try {
      const response = await PricingAPI.getProductDiscounts(product._id, true);
      if (response.success) {
        setDiscounts(response.data.discounts);
      }
    } catch (error) {
      console.error('Error fetching discounts:', error);
      toast({
        title: 'Error',
        description: 'Failed to load discount history',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddDiscount = async () => {
    try {
      await PricingAPI.addTimeBasedDiscount(product._id, {
        discountPercentage: parseFloat(newDiscount.discountPercentage),
        effectiveFrom: newDiscount.effectiveFrom,
        effectiveTo: newDiscount.effectiveTo,
        reason: newDiscount.reason,
        updateSellingPrice: true
      });

      toast({
        title: 'Success',
        description: 'Discount added successfully'
      });

      fetchDiscounts();
      onDiscountUpdate();
      setNewDiscount({
        discountPercentage: '',
        effectiveFrom: '',
        effectiveTo: '',
        reason: ''
      });
    } catch (error) {
      console.error('Error adding discount:', error);
      toast({
        title: 'Error',
        description: 'Failed to add discount',
        variant: 'destructive'
      });
    }
  };

  const handleDeleteDiscount = async (discountId: string) => {
    try {
      await PricingAPI.deleteDiscount(product._id, discountId);
      toast({
        title: 'Success',
        description: 'Discount deleted successfully'
      });
      fetchDiscounts();
      onDiscountUpdate();
    } catch (error) {
      console.error('Error deleting discount:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete discount',
        variant: 'destructive'
      });
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-background rounded-lg shadow-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b sticky top-0 bg-background">
          <h3 className="text-lg font-semibold">Discount History - {product.name}</h3>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="h-8 w-8 p-0"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="p-4 space-y-6">
          {/* Current Discount Status */}
          <div className="bg-muted p-4 rounded-lg">
            <h4 className="font-medium mb-2">Current Discount Status</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Effective Discount</p>
                <p className="text-lg font-semibold">
                  {product.effectiveDiscount ?
                    `${product.effectiveDiscount.discountPercentage}% (${product.effectiveDiscount.type})` :
                    'No active discount'}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Calculated Price</p>
                <p className="text-lg font-semibold">
                  ₹{(product.calculatedSellingPrice || product.sellingPrice).toLocaleString()}
                </p>
              </div>
            </div>
          </div>

          {/* Add New Discount */}
          <div>
            <h4 className="font-medium mb-3">Add New Time-Based Discount</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">
                  Discount Percentage
                </label>
                <Input
                  type="number"
                  value={newDiscount.discountPercentage}
                  onChange={(e) => setNewDiscount(prev => ({
                    ...prev,
                    discountPercentage: e.target.value
                  }))}
                  placeholder="Enter percentage"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  Reason
                </label>
                <Input
                  value={newDiscount.reason}
                  onChange={(e) => setNewDiscount(prev => ({
                    ...prev,
                    reason: e.target.value
                  }))}
                  placeholder="e.g., Seasonal sale"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  Start Date
                </label>
                <Input
                  type="date"
                  value={newDiscount.effectiveFrom}
                  onChange={(e) => setNewDiscount(prev => ({
                    ...prev,
                    effectiveFrom: e.target.value
                  }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  End Date
                </label>
                <Input
                  type="date"
                  value={newDiscount.effectiveTo}
                  onChange={(e) => setNewDiscount(prev => ({
                    ...prev,
                    effectiveTo: e.target.value
                  }))}
                />
              </div>
            </div>
            <Button
              className="mt-4"
              onClick={handleAddDiscount}
              disabled={!newDiscount.discountPercentage || !newDiscount.effectiveFrom || !newDiscount.effectiveTo}
            >
              Add Discount
            </Button>
          </div>

          {/* Discount History */}
          <div>
            <h4 className="font-medium mb-3">Discount History</h4>
            {isLoading ? (
              <div className="flex justify-center items-center py-8">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            ) : (discounts && discounts.length === 0) ? (
              <p className="text-center text-muted-foreground py-4">
                No discount history available
              </p>
            ) : (
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Discount</TableHead>
                      <TableHead>Period</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Reason</TableHead>
                      <TableHead className="text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {discounts.map((discount) => (
                      <TableRow key={discount._id}>
                        <TableCell>{discount.discountPercentage}%</TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <div>{new Date(discount.effectiveFrom).toLocaleDateString()}</div>
                            <div className="text-muted-foreground">to</div>
                            <div>{new Date(discount.effectiveTo).toLocaleDateString()}</div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${discount.isActive
                            ? 'bg-green-100 text-green-800'
                            : new Date(discount.effectiveTo) < new Date()
                              ? 'bg-gray-100 text-gray-800'
                              : 'bg-yellow-100 text-yellow-800'
                            }`}>
                            {discount.isActive ? 'Active' :
                              new Date(discount.effectiveTo) < new Date() ? 'Expired' : 'Scheduled'}
                          </span>
                        </TableCell>
                        <TableCell>{discount.reason || '-'}</TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteDiscount(discount._id)}
                            disabled={discount.isActive}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function PricingPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [isAddPriceModalOpen, setIsAddPriceModalOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [selectedProductToDelete, setSelectedProductToDelete] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [products, setProducts] = useState<ProductPricing[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 10,
    pages: 1
  });
  const [isTimeBasedDiscount, setIsTimeBasedDiscount] = useState(false);
  const [newPrice, setNewPrice] = useState({
    productId: '',
    basePrice: '',
    sellingPrice: '',
    taxRate: '',
    discount: '',
    effectiveFrom: '',
    effectiveTo: '',
    reason: ''
  });

  // Stats
  const [stats, setStats] = useState({
    activeCount: 0,
    scheduledCount: 0,
    expiredCount: 0,
    averageDiscount: 0
  });

  const [selectedProductForDiscounts, setSelectedProductForDiscounts] = useState<ProductPricing | null>(null);

  // Fetch products with pricing
  const fetchProducts = async (page = 1) => {
    setIsLoading(true);
    try {
      const response = await PricingAPI.getAllProductsWithPricing({
        search: searchQuery,
        category: selectedCategory || undefined,
        page,
        limit: pagination.limit,
        showDiscontinued: true
      });

      if (response.success) {
        setProducts(response.data.products);
        setPagination(response.data.pagination);

        // Extract unique categories
        const uniqueCategories = Array.from(
          new Set(
            response.data.products.map(product =>
              product.category ? product.category.name : 'Uncategorized'
            )
          )
        );
        setCategories(uniqueCategories);

        // Calculate stats
        const active = response.data.products.filter(p => p.isActive).length;
        const withDiscount = response.data.products.filter(p => p.discount > 0 || (p.effectiveDiscount && p.effectiveDiscount.discountPercentage > 0)).length;

        // Count scheduled discounts from API response
        const scheduled = response.data.products.filter(p =>
          p.timeBasedDiscounts &&
          p.timeBasedDiscounts.some(d =>
            new Date(d.effectiveFrom) > new Date()
          )
        ).length;

        // Count expired discounts from API response
        const expired = response.data.products.filter(p =>
          p.timeBasedDiscounts &&
          p.timeBasedDiscounts.some(d =>
            new Date(d.effectiveTo) < new Date() &&
            d.isActive === false
          )
        ).length;

        // Calculate average discount including effective discounts
        const totalDiscount = response.data.products.reduce(
          (sum, product) => {
            // Use the effective discount if available, otherwise use the permanent discount
            const discountValue = product.effectiveDiscount ?
              product.effectiveDiscount.discountPercentage :
              (product.discount || 0);
            return sum + discountValue;
          },
          0
        );
        const avgDiscount = withDiscount ? (totalDiscount / withDiscount) : 0;

        setStats({
          activeCount: active,
          scheduledCount: scheduled || 0,
          expiredCount: expired || 0,
          averageDiscount: avgDiscount
        });
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      toast({
        title: 'Error',
        description: 'Failed to load pricing data',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch when search or filter changes
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchProducts(1); // Reset to first page
    }, 500);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, selectedCategory]);

  // Handle adding a new price or updating a time-based discount
  const handleAddPrice = async () => {
    setIsSubmitting(true);
    try {
      // For adding a time-based discount
      if (isTimeBasedDiscount && newPrice.effectiveFrom && newPrice.effectiveTo) {
        await PricingAPI.addTimeBasedDiscount(newPrice.productId, {
          discountPercentage: parseFloat(newPrice.discount),
          effectiveFrom: newPrice.effectiveFrom,
          effectiveTo: newPrice.effectiveTo,
          reason: newPrice.reason || 'Promotional discount',
          updateSellingPrice: true
        });

        toast({
          title: 'Success',
          description: 'Discount schedule added successfully',
        });
      }
      // For updating base price
      else {
        await PricingAPI.updateBasePrice(newPrice.productId, {
          basePrice: parseFloat(newPrice.basePrice),
          taxRate: newPrice.taxRate ? parseFloat(newPrice.taxRate) : undefined,
          discount: newPrice.discount ? parseFloat(newPrice.discount) : undefined,
          updateSellingPrice: true,
          reason: newPrice.reason || 'Price update'
        });

        toast({
          title: 'Success',
          description: 'Price updated successfully',
        });
      }

      // Reset form and close modal
      setIsAddPriceModalOpen(false);
      resetPriceForm();
      fetchProducts(); // Refresh the list
    } catch (error) {
      console.error('Error updating price:', error);
      toast({
        title: 'Error',
        description: 'Failed to update pricing',
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle deleting a time-based discount
  const handleDeleteDiscount = async (productId: string, discountId: string) => {
    try {
      await PricingAPI.deleteDiscount(productId, discountId);
      toast({
        title: 'Success',
        description: 'Discount deleted successfully',
      });
      fetchProducts(); // Refresh the list
    } catch (error) {
      console.error('Error deleting discount:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete discount',
        variant: 'destructive'
      });
    } finally {
      setIsDeleteConfirmOpen(false);
      setSelectedProductToDelete(null);
    }
  };

  // Reset the price form
  const resetPriceForm = () => {
    setNewPrice({
      productId: '',
      basePrice: '',
      sellingPrice: '',
      taxRate: '',
      discount: '',
      effectiveFrom: '',
      effectiveTo: '',
      reason: ''
    });
    setIsTimeBasedDiscount(false);
  };

  // Calculate selling price based on base price, discount and tax
  // Updated to match API documentation formula:
  // sellingPrice = (basePrice * (1 - discountPercentage/100)) + tax
  // where tax = discountedPrice * (taxRate/100)
  const calculateSellingPrice = (basePrice: string, discount: string, taxRate: string): string => {
    if (!basePrice) return '';

    const basePriceNum = parseFloat(basePrice);
    const discountNum = parseFloat(discount || '0');
    const taxRateNum = parseFloat(taxRate || '0');

    // Apply discount first
    const discountedPrice = basePriceNum * (1 - discountNum / 100);

    // Calculate tax
    const tax = discountedPrice * (taxRateNum / 100);

    // Final selling price
    const finalPrice = discountedPrice + tax;

    return finalPrice.toFixed(2);
  };

  // Update form values and calculate selling price
  const updatePriceForm = (field: string, value: string) => {
    const updatedPrice = { ...newPrice, [field]: value };

    // Auto-calculate selling price when base price, discount or tax is changed
    if (field === 'basePrice' || field === 'discount' || field === 'taxRate') {
      updatedPrice.sellingPrice = calculateSellingPrice(
        updatedPrice.basePrice,
        updatedPrice.discount,
        updatedPrice.taxRate
      );
    }

    setNewPrice(updatedPrice);
  };

  // Format date for display
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  // Calculate discount percentage
  const calculateDiscountPercentage = (basePrice: number, sellingPrice: number) => {
    if (!basePrice || !sellingPrice || basePrice === sellingPrice) return 0;
    return ((basePrice - sellingPrice) / basePrice) * 100;
  };

  // When editing a product, initialize the time-based discount checkbox
  useEffect(() => {
    if (newPrice.effectiveFrom && newPrice.effectiveTo) {
      setIsTimeBasedDiscount(true);
    } else {
      setIsTimeBasedDiscount(false);
    }
  }, [newPrice.effectiveFrom, newPrice.effectiveTo]);

  // Handle time-based discount toggle
  const handleTimeBasedDiscountToggle = (isChecked: boolean) => {
    setIsTimeBasedDiscount(isChecked);

    if (isChecked) {
      // Set default dates (today and a month from now)
      const today = new Date().toISOString().split('T')[0];
      const nextMonth = new Date();
      nextMonth.setMonth(nextMonth.getMonth() + 1);
      const nextMonthStr = nextMonth.toISOString().split('T')[0];

      updatePriceForm('effectiveFrom', today);
      updatePriceForm('effectiveTo', nextMonthStr);
    } else {
      updatePriceForm('effectiveFrom', '');
      updatePriceForm('effectiveTo', '');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold">Pricing Management</h1>
          <p className="text-muted-foreground">Set and manage product prices</p>
        </div>
        <Button onClick={() => setIsAddPriceModalOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Add Price
        </Button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between space-y-0">
              <p className="text-sm font-medium text-muted-foreground">Active Prices</p>
              <div className="h-8 w-8 rounded-full bg-green-100 flex items-center justify-center">
                <DollarSign className="h-4 w-4 text-green-600" />
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold">{stats.activeCount}</p>
                <p className="text-xs text-muted-foreground">Currently active</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between space-y-0">
              <p className="text-sm font-medium text-muted-foreground">Scheduled Updates</p>
              <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                <Calendar className="h-4 w-4 text-blue-600" />
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold">{stats.scheduledCount}</p>
                <p className="text-xs text-muted-foreground">Upcoming changes</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between space-y-0">
              <p className="text-sm font-medium text-muted-foreground">Expired Prices</p>
              <div className="h-8 w-8 rounded-full bg-red-100 flex items-center justify-center">
                <Clock className="h-4 w-4 text-red-600" />
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold">{stats.expiredCount}</p>
                <p className="text-xs text-muted-foreground">Need attention</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between space-y-0">
              <p className="text-sm font-medium text-muted-foreground">Avg. Discount</p>
              <div className="h-8 w-8 rounded-full bg-purple-100 flex items-center justify-center">
                <ArrowDown className="h-4 w-4 text-purple-600" />
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold">{stats.averageDiscount.toFixed(1)}%</p>
                <p className="text-xs text-muted-foreground">Off base prices</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search and filter */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between items-center">
        <div className="relative w-full sm:w-auto">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search products..."
            className="pl-8 w-full sm:w-[300px]"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="flex gap-2 w-full sm:w-auto">
          <select
            className="h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
          >
            <option value="">All Categories</option>
            {categories.map((category) => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
          </select>
          <Button variant="outline" size="sm">
            <Filter className="h-4 w-4 mr-2" />
            More Filters
          </Button>
          <Button variant="outline" size="sm">
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Pricing table */}
      <div className="rounded-md border overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Product</TableHead>
                <TableHead className="hidden md:table-cell">Category</TableHead>
                <TableHead>
                  <div className="flex items-center gap-1">
                    Base Price (₹)
                    <ArrowDown className="h-3 w-3" />
                  </div>
                </TableHead>
                <TableHead>Selling Price (₹)</TableHead>
                <TableHead className="hidden md:table-cell">Unit</TableHead>
                <TableHead className="hidden lg:table-cell">Discount</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-10">
                    <div className="flex justify-center items-center">
                      <Loader2 className="h-6 w-6 mr-2 animate-spin" />
                      <span>Loading pricing data...</span>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (products && products.length === 0) ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-6 text-muted-foreground">
                    No pricing data found. Try adjusting your search.
                  </TableCell>
                </TableRow>
              ) : (
                products.map((product) => (
                  <TableRow key={product._id}>
                    <TableCell className="font-medium">
                      <div>{product.name}</div>
                      <div className="text-xs text-muted-foreground md:hidden">
                        {product.category?.name || 'Uncategorized'}
                      </div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      {product.category?.name || 'Uncategorized'}
                    </TableCell>
                    <TableCell>₹{(product.basePrice || 0).toLocaleString()}</TableCell>
                    <TableCell>
                      <div className="flex items-center">
                        ₹{(product.calculatedSellingPrice || product.sellingPrice || 0).toLocaleString()}
                      </div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      {product.unit}
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 hover:bg-muted"
                        onClick={() => setSelectedProductForDiscounts(product)}
                      >
                        <div className="flex items-center">
                          {product.effectiveDiscount ? (
                            <div className="text-left">
                              <div className="text-xs font-medium">
                                {product.effectiveDiscount.discountPercentage}%
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {product.effectiveDiscount.type === 'timed' ? 'Timed' : 'Permanent'}
                              </div>
                            </div>
                          ) : product.discount > 0 ? (
                            <div className="text-left">
                              <div className="text-xs font-medium">{product.discount}%</div>
                              <div className="text-xs text-muted-foreground">Permanent</div>
                            </div>
                          ) : (
                            <span className="text-xs text-muted-foreground">View Discounts</span>
                          )}
                          <History className="h-4 w-4 ml-1" />
                        </div>
                      </Button>
                    </TableCell>
                    <TableCell>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${product.isActive
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                        }`}>
                        {product.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setNewPrice({
                              productId: product._id,
                              basePrice: product.basePrice.toString(),
                              sellingPrice: (product.calculatedSellingPrice || product.sellingPrice).toString(),
                              taxRate: product.taxRate.toString(),
                              discount: product.discount.toString(),
                              effectiveFrom: '',
                              effectiveTo: '',
                              reason: ''
                            });
                            setIsTimeBasedDiscount(false);
                            setIsAddPriceModalOpen(true);
                          }}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedProductToDelete(product._id);
                            setIsDeleteConfirmOpen(true);
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Pagination */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="text-sm text-muted-foreground">
          Showing <strong>{products.length ? (pagination.page - 1) * pagination.limit + 1 : 0}-{Math.min(pagination.page * pagination.limit, pagination.total)}</strong> of <strong>{pagination.total}</strong> products
        </div>
        <div className="flex gap-1">
          <Button
            variant="outline"
            size="sm"
            disabled={pagination.page <= 1 || isLoading}
            onClick={() => fetchProducts(pagination.page - 1)}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={pagination.page >= pagination.pages || isLoading}
            onClick={() => fetchProducts(pagination.page + 1)}
          >
            Next
          </Button>
        </div>
      </div>

      {/* Add/Edit Price Modal */}
      {isAddPriceModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-background rounded-lg shadow-lg w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">
                {newPrice.productId ? 'Edit Price' : 'Add New Price'}
              </h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setIsAddPriceModalOpen(false);
                  resetPriceForm();
                }}
                className="h-8 w-8 p-0"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1" htmlFor="product">
                  Product
                </label>
                <select
                  id="product"
                  className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={newPrice.productId}
                  onChange={(e) => updatePriceForm('productId', e.target.value)}
                  disabled={!!newPrice.productId}
                >
                  <option value="">Select Product</option>
                  {products.map((product) => (
                    <option key={product._id} value={product._id}>
                      {product.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1" htmlFor="basePrice">
                    Base Price (₹)
                  </label>
                  <Input
                    id="basePrice"
                    type="number"
                    placeholder="0.00"
                    value={newPrice.basePrice}
                    onChange={(e) => updatePriceForm('basePrice', e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1" htmlFor="sellingPrice">
                    Selling Price (₹)
                  </label>
                  <Input
                    id="sellingPrice"
                    type="number"
                    placeholder="0.00"
                    value={newPrice.sellingPrice}
                    disabled
                    className="bg-muted"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1" htmlFor="taxRate">
                    Tax Rate (%)
                  </label>
                  <Input
                    id="taxRate"
                    type="number"
                    placeholder="0.00"
                    value={newPrice.taxRate}
                    onChange={(e) => updatePriceForm('taxRate', e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1" htmlFor="discount">
                    Discount (%)
                  </label>
                  <Input
                    id="discount"
                    type="number"
                    placeholder="0.00"
                    value={newPrice.discount}
                    onChange={(e) => updatePriceForm('discount', e.target.value)}
                  />
                </div>
              </div>

              <div>
                <label className="flex items-center text-sm font-medium mb-2">
                  <input
                    type="checkbox"
                    className="mr-2"
                    checked={isTimeBasedDiscount}
                    onChange={(e) => handleTimeBasedDiscountToggle(e.target.checked)}
                  />
                  Schedule a time-based discount
                </label>
              </div>

              {isTimeBasedDiscount && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1" htmlFor="effectiveFrom">
                      Effective From
                    </label>
                    <Input
                      id="effectiveFrom"
                      type="date"
                      value={newPrice.effectiveFrom}
                      onChange={(e) => updatePriceForm('effectiveFrom', e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1" htmlFor="effectiveTo">
                      Effective To
                    </label>
                    <Input
                      id="effectiveTo"
                      type="date"
                      value={newPrice.effectiveTo}
                      onChange={(e) => updatePriceForm('effectiveTo', e.target.value)}
                    />
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium mb-1" htmlFor="reason">
                  Reason for Change
                </label>
                <Input
                  id="reason"
                  placeholder="e.g., Market adjustment, promotion, etc."
                  value={newPrice.reason}
                  onChange={(e) => updatePriceForm('reason', e.target.value)}
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t">
              <Button
                variant="outline"
                onClick={() => {
                  setIsAddPriceModalOpen(false);
                  resetPriceForm();
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleAddPrice}
                disabled={isSubmitting || !newPrice.productId || !newPrice.basePrice}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Plus className="h-4 w-4 mr-2" />
                    {newPrice.productId ? 'Update Price' : 'Add Price'}
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteConfirmOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-background rounded-lg shadow-lg w-full max-w-md">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <AlertTriangle className="h-6 w-6 text-red-500" />
                <h3 className="text-lg font-semibold">Confirm Delete</h3>
              </div>
              <p className="mb-4">Are you sure you want to delete this pricing? This action cannot be undone.</p>
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsDeleteConfirmOpen(false);
                    setSelectedProductToDelete(null);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => {
                    toast({
                      title: "Feature not implemented",
                      description: "Deleting prices is not fully implemented in this demo",
                    });
                    setIsDeleteConfirmOpen(false);
                    setSelectedProductToDelete(null);
                  }}
                >
                  Delete
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Discount Details Modal */}
      {selectedProductForDiscounts && (
        <DiscountDetails
          product={selectedProductForDiscounts}
          onClose={() => setSelectedProductForDiscounts(null)}
          onDiscountUpdate={() => fetchProducts()}
        />
      )}
    </div>
  );
} 